export enum ReportType {
  INCOME_STATEMENT = 'income_statement',
  BALANCE_SHEET = 'balance_sheet',
  CASH_FLOW = 'cash_flow',
  CHANGE_IN_EQUITY = 'change_in_equity',
  TRIAL_BALANCE = 'trial_balance',
  TAX_RETURN = 'tax_return',
  ZAKAT_RETURN = 'zakat_return',
  OTHER = 'other',
  /** @deprecated legacy uploads stored as custom before structured types existed */
  CUSTOM = 'custom',
  /** @deprecated legacy report types — always shown under the Other tab */
  INCOME_SUMMARY = 'income_summary',
  EXPENSE_SUMMARY = 'expense_summary',
  INCOME_VS_EXPENSE = 'income_vs_expense',
}

export enum ProcessingStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  PENDING_APPROVAL = 'pending_approval',
  APPROVED = 'approved',
  FAILED = 'failed',
  READY = 'ready',
}

export enum SourceType {
  EXCEL = 'excel',
  PDF = 'pdf',
}

export enum ClientDecision {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

export interface SubmitClientDecisionRequest {
  decision: ClientDecision.APPROVED | ClientDecision.REJECTED;
  note?: string;
}

export interface Report {
  id: string;
  organization_id: string;
  name: string;
  type: ReportType;
  processing_status: ProcessingStatus;
  source_type: SourceType;
  from_date: string | null;
  to_date: string | null;
  year?: number | null;
  quarter?: number | null;
  filename: string | null;
  client_decision?: ClientDecision | null;
  client_decision_at?: string | null;
  client_note?: string | null;
  created_at: string;
}

export interface ReportDetails {
  id: string;
  organization_id: string;
  asset_id: string | null;
  pdf_asset_id?: string | null;
  filename: string | null;
  pdf_filename?: string | null;
  file_path: string | null;
  type: ReportType;
  processing_status: ProcessingStatus;
  source_type: SourceType | null;
  processing_error?: string | null;
  data: ReportDataTypes | null;
  name?: string | null;
  from_date?: string | null;
  to_date?: string | null;
  year?: number | null;
  quarter?: number | null;
  client_decision?: ClientDecision | null;
  client_decision_at?: string | null;
  client_note?: string | null;
  created_at: string;
}

export interface CreateReport {
  organization_id: string;
  asset_id: string;
  type: ReportType;
  from_date?: string;
  to_date?: string;
  year?: number;
  quarter?: number;
}

export interface ReportListResponse {
  result_list: Report[];
  total_count: number;
}

// ─── Data shape interfaces ───────────────────────────────────────────────────

export interface ReportLineItem {
  name_en: string;
  name_ar: string;
  amount: number | string;
  pct?: number | string;
}

export interface FixedAssetItem {
  name_en: string;
  name_ar: string;
  cost?: number | string;
  accumulated_depreciation?: number | string;
  net: number | string;
  pct?: number | string;
}

/** Shared Excel section shape (Income Statement, Balance Sheet, Cash Flow). */
export interface ReportExcelSection {
  section_type: string;
  title_en?: string;
  title_ar?: string;
  header_amount?: number | string;
  items?: ReportLineItem[];
  fixed_items?: FixedAssetItem[];
  subtotal_label?: string;
  subtotal?: number | string;
}

export interface AdjustmentItem {
  name_en: string;
  name_ar: string;
  amount: number;
}

export interface EquityRow {
  capital: number;
  retained_earnings: number;
  legal_reserve: number;
  total: number;
}

export interface TrialBalanceAccount {
  name: string;
  opening_debit: number;
  opening_credit: number;
  ending_debit: number;
  ending_credit: number;
}

export type IncomeStatementSectionType = 'revenue' | 'cogs' | 'operating' | 'operating_expenses' | string;

export type IncomeStatementSection = ReportExcelSection;

export interface IncomeStatementData {
  name?: string;
  description?: string | null;
  currency: string;
  from_date: string;
  to_date: string;
  revenue_items?: ReportLineItem[];
  cogs_items?: ReportLineItem[];
  sections?: IncomeStatementSection[];
  gross_profit: number | string;
  gross_profit_label?: string;
  expenses: ReportLineItem[];
  total_expenses?: number | string;
  total_expenses_label?: string;
  net_operation_income: number | string;
  net_operation_income_label?: string;
  other_revenues?: number | string;
  other_revenues_label?: string;
  other_expenses?: number | string;
  other_expenses_label?: string;
  net_income: number | string;
  net_income_label?: string;
  /** @deprecated use revenue_items */
  net_sales?: number | string;
  /** @deprecated use revenue_items */
  cost_of_service?: number | string;
}

export interface BalanceSheetData {
  name?: string;
  currency: string;
  from_date: string;
  to_date: string;
  sections?: ReportExcelSection[];
  current_assets?: ReportLineItem[];
  total_current_assets?: number | string;
  total_current_assets_label?: string;
  fixed_assets?: FixedAssetItem[];
  net_fixed_assets?: number | string;
  net_fixed_assets_label?: string;
  total_assets?: number | string;
  total_assets_label?: string;
  current_liabilities?: ReportLineItem[];
  total_liabilities?: number | string;
  total_liabilities_label?: string;
  capital?: number | string;
  equity_items?: ReportLineItem[];
  legal_reserve?: number | string;
  total_owner_equity?: number | string;
  total_owner_equity_label?: string;
  total_liabilities_equity?: number | string;
  total_liabilities_equity_label?: string;
  balance?: number | string;
  balance_label?: string;
  /** @deprecated use equity_items */
  current_year_profit?: number | string;
  /** @deprecated use equity_items */
  retained_earnings?: number | string;
}

export interface CashFlowData {
  name?: string;
  currency: string;
  from_date: string;
  to_date: string;
  sections?: ReportExcelSection[];
  net_income?: number | string;
  net_income_label?: string;
  adjustments?: AdjustmentItem[];
  change_in_cash?: number | string;
  change_in_cash_label?: string;
  beginning_cash?: number | string;
  beginning_cash_label?: string;
  ending_cash?: number | string;
  ending_cash_label?: string;
}

export interface ChangeInEquityData {
  name?: string;
  currency: string;
  from_date: string;
  to_date: string;
  opening_balance: EquityRow;
  net_profit: EquityRow;
  reserve_formation: EquityRow;
  prior_adjustments: EquityRow;
  dividends: EquityRow;
  capital_additions: EquityRow;
  closing_balance: EquityRow;
}

export interface TrialBalanceData {
  name?: string;
  currency: string;
  from_date: string;
  to_date: string;
  accounts: TrialBalanceAccount[];
  total_opening_debit: number;
  total_opening_credit: number;
  total_ending_debit: number;
  total_ending_credit: number;
}

export interface TaxReturnData {
  name?: string;
  year: number;
  quarter: number;
  from_date?: string;
  to_date?: string;
}

export type ReportDataTypes =
  | IncomeStatementData
  | BalanceSheetData
  | CashFlowData
  | ChangeInEquityData
  | TrialBalanceData
  | TaxReturnData;
